import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchAgendaTimeline,
  createAgendaEvent,
  updateAgendaEvent,
  deleteAgendaEvent,
} from "../api/agenda.api";
import { fetchCustomers } from "../api/customers.api";
import { fetchProjects } from "../api/projects.api";
import {
  CalendarDays,
  Clock,
  Users,
  Plus,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  SlidersHorizontal,
  Pencil,
  Loader2,
  Trash2,
  MapPin,
  Bell,
  CreditCard,
  Briefcase,
  ExternalLink,
  Eye,
  Lock,
  Mail,
} from "lucide-react";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function Pill({ children, tone = "neutral" }) {
  const tones = {
    neutral: "bg-slate-100 text-slate-700 ring-slate-200",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    yellow: "bg-amber-50 text-amber-700 ring-amber-100",
    blue: "bg-sky-50 text-sky-700 ring-sky-100",
    red: "bg-rose-50 text-rose-700 ring-rose-100",
    purple: "bg-violet-50 text-violet-700 ring-violet-100",
    indigo: "bg-slate-50 text-slate-800 ring-slate-200",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ring-1",
        tones[tone] || tones.neutral
      )}
    >
      {children}
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-medium text-slate-500">{label}</div>
          <div className="mt-1 text-xl font-bold text-slate-900">{value}</div>
          {sub ? <div className="mt-1 text-xs text-slate-500">{sub}</div> : null}
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-2">
          <Icon className="h-5 w-5 text-slate-700" />
        </div>
      </div>
    </div>
  );
}

function ModalShell({ title, subtitle, onClose, children }) {
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <div className="text-lg font-bold text-slate-900">{title}</div>
            {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
          </div>
          <button
            onClick={onClose}
            className="rounded-xl border border-slate-200 bg-white p-2 hover:bg-slate-50"
            aria-label="Fermer"
          >
            <X className="h-4 w-4 text-slate-700" />
          </button>
        </div>
        <div className="max-h-[80vh] overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <div className="h-4 w-44 animate-pulse rounded bg-slate-100" />
              <div className="h-3 w-24 animate-pulse rounded bg-slate-100" />
            </div>
            <div className="h-7 w-20 animate-pulse rounded-full bg-slate-100" />
          </div>
          <div className="mt-4 space-y-2">
            <div className="h-3 w-full animate-pulse rounded bg-slate-100" />
            <div className="h-3 w-5/6 animate-pulse rounded bg-slate-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatMoney(amount) {
  if (amount == null) return "—";
  return Number(amount).toLocaleString("fr-FR") + " FCFA";
}

function getMonthDays(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const days = [];

  const startPadding = (firstDay.getDay() + 6) % 7;
  for (let i = startPadding - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push({ date: d, isCurrentMonth: false });
  }

  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push({ date: new Date(year, month, i), isCurrentMonth: true });
  }

  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
  }

  return days;
}

// Configuration des types d'événements
const eventTypeConfig = {
  MEETING: { label: "Réunion", tone: "blue", icon: Users },
  CALL: { label: "Appel", tone: "green", icon: Bell },
  TASK: { label: "Tâche", tone: "yellow", icon: Clock },
  DEADLINE: { label: "Échéance", tone: "red", icon: Bell },
  OTHER: { label: "Autre", tone: "neutral", icon: CalendarDays },
  // Nouveaux types auto-générés
  PAYMENT_DUE: { label: "Paiement dû", tone: "purple", icon: CreditCard },
  PRESTATION_DUE: { label: "Prestation", tone: "indigo", icon: Briefcase },
  COMMUNICATION: { label: "Campagne", tone: "purple", icon: Mail },
};

// Types éditables (manuels)
const editableTypes = ["MEETING", "CALL", "TASK", "DEADLINE", "OTHER", "COMMUNICATION"];

export default function Agenda() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("edit"); // "edit" | "view"
  const [editingEvent, setEditingEvent] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [view, setView] = useState("list");
  const [currentDate, setCurrentDate] = useState(new Date());

  const [searchDraft, setSearchDraft] = useState("");
  const [filters, setFilters] = useState({ search: "", eventType: "", sort: "start_asc" });

  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: "",
    project_id: "",
    title: "",
    description: "",
    event_type: "MEETING",
    start_time: "",
    end_time: "",
    location: "",
    reminder_minutes: 30,
  });
  const isCampaignEvent = editingEvent?.source_type === "COMM_CAMPAIGN";

  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => ({ ...f, search: searchDraft }));
    }, 300);
    return () => clearTimeout(t);
  }, [searchDraft]);

  useEffect(() => {
    loadData();
  }, [filters.eventType, currentDate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startDate = new Date(year, month, 1).toISOString().slice(0, 10);
      const endDate = new Date(year, month + 1, 0).toISOString().slice(0, 10);

      const [eventsData, customersData, projectsData] = await Promise.all([
        fetchAgendaTimeline(startDate, endDate, filters.eventType || null),
        fetchCustomers(),
        fetchProjects({}),
      ]);
      setEvents(Array.isArray(eventsData) ? eventsData : []);
      setCustomers(Array.isArray(customersData) ? customersData : []);
      setProjects(Array.isArray(projectsData) ? projectsData : []);
    } catch (err) {
      console.error("Erreur:", err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [currentDate, filters.eventType]);

  function resetForm(defaultDate = null) {
    const baseDate = defaultDate ? new Date(defaultDate + "T09:00:00") : new Date();
    if (!defaultDate) baseDate.setTime(baseDate.getTime() + 60 * 60 * 1000);
    const defaultEnd = new Date(baseDate.getTime() + 60 * 60 * 1000);

    setFormData({
      customer_id: "",
      project_id: "",
      title: "",
      description: "",
      event_type: "MEETING",
      start_time: baseDate.toISOString().slice(0, 16),
      end_time: defaultEnd.toISOString().slice(0, 16),
      location: "",
      reminder_minutes: 30,
    });
  }

  function openCreate(defaultDate = null) {
    resetForm(defaultDate);
    setEditingEvent(null);
    setModalMode("edit");
    setShowModal(true);
  }

  function openEdit(event) {
    // Si readonly, ouvrir en mode view
    if (event.readonly) {
      setEditingEvent(event);
      setModalMode("view");
      setShowModal(true);
      return;
    }

    // Mode édition pour événements manuels
    setEditingEvent(event);
    setModalMode("edit");
    setFormData({
      customer_id: event.customer_id || "",
      project_id: event.project_id || "",
      title: event.title || "",
      description: event.description || "",
      event_type: event.event_type || "MEETING",
      start_time: event.start_time ? event.start_time.slice(0, 16) : "",
      end_time: event.end_time ? event.end_time.slice(0, 16) : "",
      location: event.location || "",
      reminder_minutes: event.reminder_minutes ?? 30,
    });
    setShowModal(true);
  }

  // Clic sur une case jour du calendrier
  function handleDayClick(dateStr, dayEvents, e) {
    // Si clic sur un événement, ne pas déclencher le clic jour
    if (e.target.closest("[data-event-btn]")) return;

    // Si pas d'événements ou clic sur zone vide, créer un nouvel événement
    openCreate(dateStr);
  }

  // Navigation vers la page de détail de l'entité référencée
  function navigateToRef(event) {
    if (!event.ref_type || !event.ref_id) return;

    switch (event.ref_type) {
      case "ORDER":
        navigate(`/orders?id=${event.ref_id}`);
        break;
      case "PRESTATION":
        navigate(`/prestations?id=${event.ref_id}`);
        break;
      case "INSTALLMENT_PLAN":
        navigate(`/installments?plan_id=${event.ref_id}${event.customer_uid ? `&customer_uid=${event.customer_uid}` : ''}`);
        break;
      case "INSTALLMENT":
        navigate(`/installments?plan_id=${event.ref_id}${event.customer_uid ? `&customer_uid=${event.customer_uid}` : ''}`);
        break;
      case "COMM_CAMPAIGN":
        navigate(`/communications?campaignId=${event.source_id || ""}`);
        break;
      default:
        console.warn("Unknown ref_type:", event.ref_type);
    }
    setShowModal(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!formData.title?.trim() || !formData.start_time) return;

    setSaving(true);
    try {
      const payload = {
        ...formData,
        customer_id: formData.customer_id || null,
        project_id: formData.project_id || null,
        source_type: editingEvent?.source_type || null,
        source_id: editingEvent?.source_id || null,
      };

      if (editingEvent && editingEvent.ref_id) {
        await updateAgendaEvent(editingEvent.ref_id, payload);
      } else {
        await createAgendaEvent(payload);
      }
      setShowModal(false);
      setEditingEvent(null);
      resetForm();
      await loadData();
    } catch (err) {
      alert(err?.message || "Erreur");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!editingEvent || !editingEvent.ref_id) return;
    if (editingEvent.readonly) return;
    if (!confirm("Supprimer cet événement ?")) return;

    setDeleting(true);
    try {
      await deleteAgendaEvent(editingEvent.ref_id);
      setShowModal(false);
      setEditingEvent(null);
      await loadData();
    } catch (err) {
      alert(err?.message || "Erreur suppression");
    } finally {
      setDeleting(false);
    }
  }

  async function handleMarkDone() {
    if (!editingEvent || !editingEvent.ref_id) return;
    setSaving(true);
    try {
      await updateAgendaEvent(editingEvent.ref_id, { status: 'COMPLETED' });
      setShowModal(false);
      setEditingEvent(null);
      await loadData();
    } catch (err) {
      alert(err?.message || "Erreur mise à jour statut");
    } finally {
      setSaving(false);
    }
  }

  const computed = useMemo(() => {
    const total = events.length;
    const today = new Date().toISOString().slice(0, 10);
    const todayEvents = events.filter((e) => e.start_time?.slice(0, 10) === today).length;
    const meetings = events.filter((e) => e.event_type === "MEETING").length;
    const paymentsDue = events.filter((e) => e.event_type === "PAYMENT_DUE").length;
    const upcoming = events.filter((e) => e.start_time > new Date().toISOString()).length;
    return { total, todayEvents, meetings, paymentsDue, upcoming };
  }, [events]);

  const filteredEvents = useMemo(() => {
    let list = [...events];

    if (filters.search) {
      const q = filters.search.toLowerCase();
      list = list.filter(
        (e) =>
          e.title?.toLowerCase().includes(q) ||
          e.description?.toLowerCase().includes(q) ||
          e.customer_name?.toLowerCase().includes(q)
      );
    }

    const s = filters.sort;
    if (s === "start_asc") list.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
    if (s === "start_desc") list.sort((a, b) => (b.start_time || "").localeCompare(a.start_time || ""));
    if (s === "title_asc") list.sort((a, b) => (a.title || "").localeCompare(b.title || ""));

    return list;
  }, [events, filters]);

  const monthDays = useMemo(() => {
    return getMonthDays(currentDate.getFullYear(), currentDate.getMonth());
  }, [currentDate]);

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      const date = e.start_time?.slice(0, 10);
      if (date) {
        if (!map[date]) map[date] = [];
        map[date].push(e);
      }
    });
    return map;
  }, [events]);

  const monthName = currentDate.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  function prevMonth() {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  }

  function nextMonth() {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  }

  function goToday() {
    setCurrentDate(new Date());
  }

  // Rendu d'un événement dans le calendrier
  function renderCalendarEvent(event) {
    const cfg = eventTypeConfig[event.event_type] || eventTypeConfig.OTHER;
    const toneClasses = {
      blue: "bg-sky-100 text-sky-700 hover:bg-sky-200",
      green: "bg-emerald-100 text-emerald-700 hover:bg-emerald-200",
      yellow: "bg-amber-100 text-amber-700 hover:bg-amber-200",
      red: "bg-rose-100 text-rose-700 hover:bg-rose-200",
      neutral: "bg-slate-100 text-slate-700 hover:bg-slate-200",
      purple: "bg-violet-100 text-violet-700 hover:bg-violet-200",
      indigo: "bg-slate-100 text-slate-800 hover:bg-slate-200",
    };

    const status = String(event.status || "").toLowerCase();
    const isPaid = status === "paid";
    const isPartial = status === "partial";

    return (
      <button
        key={event.id}
        data-event-btn
        onClick={(e) => {
          e.stopPropagation();
          openEdit(event);
        }}
        className={cx(
          "w-full text-left text-[10px] font-medium px-1.5 py-0.5 rounded truncate flex items-center gap-1",
          toneClasses[cfg.tone] || toneClasses.neutral,
          event.readonly && "opacity-90"
        )}
        title={event.title}
      >
        {event.readonly && <Lock className="h-2.5 w-2.5 flex-shrink-0" />}
        <span className="truncate">{event.title}</span>
        {event.event_type === "PAYMENT_DUE" && (isPaid || isPartial) && (
          <span
            className={cx(
              "ml-auto rounded-full px-1.5 py-0.5 text-[9px] font-semibold",
              isPaid ? "bg-emerald-200 text-emerald-900" : "bg-amber-200 text-amber-900"
            )}
          >
            {isPaid ? "PAYÉ" : "PARTIEL"}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top header */}
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <CalendarDays className="h-4 w-4" />
                <span>Gestion</span>
                <span className="text-slate-300">/</span>
                <span className="text-slate-900 font-medium">Agenda</span>
              </div>
              <h1 className="mt-2 text-2xl font-black tracking-tight text-slate-900">
                Agenda & Échéances
              </h1>
              <p className="mt-1 text-sm text-slate-600">
                Planifie tes réunions, appels et suis tes échéances de paiement.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="inline-flex rounded-2xl border border-slate-200 bg-white p-1">
                <button
                  onClick={() => setView("list")}
                  className={cx(
                    "rounded-xl px-3 py-1.5 text-sm font-medium transition",
                    view === "list" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
                  )}
                >
                  Liste
                </button>
                <button
                  onClick={() => setView("calendar")}
                  className={cx(
                    "rounded-xl px-3 py-1.5 text-sm font-medium transition",
                    view === "calendar" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
                  )}
                >
                  Calendrier
                </button>
              </div>

              <button
                onClick={() => openCreate()}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800"
              >
                <Plus className="h-4 w-4" />
                Nouvel événement
              </button>
              <button
                onClick={() => {
                  const d = new Date(currentDate);
                  d.setHours(9, 0, 0, 0);
                  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
                    .toISOString()
                    .slice(0, 16);
                  navigate(`/communications?tab=builder&scheduled=${encodeURIComponent(local)}`);
                }}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 shadow-sm hover:bg-slate-50"
              >
                <Mail className="h-4 w-4" />
                Créer campagne
              </button>
            </div>
          </div>

          {/* KPI row */}
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <KpiCard icon={CalendarDays} label="Total événements" value={computed.total} sub="Ce mois" />
            <KpiCard icon={Clock} label="Aujourd'hui" value={computed.todayEvents} sub="Événements" />
            <KpiCard icon={CreditCard} label="Paiements dus" value={computed.paymentsDue} sub="Échéances" />
            <KpiCard icon={Bell} label="À venir" value={computed.upcoming} sub="Non passés" />
          </div>

          {/* Navigation mois + Filters */}
          <div className="mt-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={prevMonth}
                className="rounded-xl border border-slate-200 bg-white p-2 hover:bg-slate-50"
              >
                <ChevronLeft className="h-4 w-4 text-slate-700" />
              </button>
              <div className="min-w-[160px] text-center">
                <span className="text-sm font-semibold text-slate-900 capitalize">{monthName}</span>
              </div>
              <button
                onClick={nextMonth}
                className="rounded-xl border border-slate-200 bg-white p-2 hover:bg-slate-50"
              >
                <ChevronRight className="h-4 w-4 text-slate-700" />
              </button>
              <button
                onClick={goToday}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                Aujourd'hui
              </button>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  placeholder="Rechercher..."
                  className="w-full rounded-2xl border border-slate-200 bg-white px-10 py-2.5 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                />
              </div>

              <div className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5">
                <SlidersHorizontal className="h-4 w-4 text-slate-500" />
                <select
                  value={filters.eventType}
                  onChange={(e) => setFilters((f) => ({ ...f, eventType: e.target.value }))}
                  className="bg-transparent text-sm text-slate-900 outline-none"
                >
                  <option value="">Tous les types</option>
                  {Object.entries(eventTypeConfig).map(([key, val]) => (
                    <option key={key} value={key}>
                      {val.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-7xl px-6 py-6">
        {loading ? (
          <SkeletonGrid />
        ) : view === "calendar" ? (
          /* Vue Calendrier */
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="grid grid-cols-7 gap-1 mb-2">
              {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((day) => (
                <div key={day} className="text-center text-xs font-semibold text-slate-500 py-2">
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {monthDays.map(({ date, isCurrentMonth }, idx) => {
                const dateStr = date.toISOString().slice(0, 10);
                const dayEvents = eventsByDate[dateStr] || [];
                const isToday = dateStr === new Date().toISOString().slice(0, 10);

                return (
                  <div
                    key={idx}
                    onClick={(e) => handleDayClick(dateStr, dayEvents, e)}
                    className={cx(
                      "min-h-[100px] rounded-xl border p-2 transition cursor-pointer group relative",
                      isCurrentMonth ? "bg-white border-slate-200 hover:border-slate-300" : "bg-slate-50 border-slate-100",
                      isToday && "ring-2 ring-slate-900"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div
                        className={cx(
                          "text-xs font-medium",
                          isCurrentMonth ? "text-slate-900" : "text-slate-400",
                          isToday && "text-slate-900 font-bold"
                        )}
                      >
                        {date.getDate()}
                      </div>
                      {/* Bouton + rapide */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openCreate(dateStr);
                        }}
                        className="opacity-0 group-hover:opacity-100 rounded-full bg-slate-900 p-0.5 text-white hover:bg-slate-800 transition"
                        title="Ajouter un événement"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="mt-1 space-y-1">
                      {dayEvents.slice(0, 3).map(renderCalendarEvent)}
                      {dayEvents.length > 3 && (
                        <div className="text-[10px] text-slate-500 px-1">
                          +{dayEvents.length - 3} autres
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50">
              <CalendarDays className="h-7 w-7 text-slate-600" />
            </div>
            <h3 className="mt-4 text-lg font-bold text-slate-900">Aucun événement</h3>
            <p className="mt-1 text-sm text-slate-600">
              Crée ton premier événement pour ce mois.
            </p>
            <button
              onClick={() => openCreate()}
              className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
            >
              <Plus className="h-4 w-4" />
              Créer un événement
            </button>
          </div>
        ) : (
          /* Vue Liste */
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filteredEvents.map((event) => {
              const cfg = eventTypeConfig[event.event_type] || eventTypeConfig.OTHER;
              const IconComp = cfg.icon || CalendarDays;

              return (
                <div
                  key={event.id}
                  className={cx(
                    "group rounded-3xl border bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md",
                    event.readonly ? "border-slate-200" : "border-slate-200"
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="truncate text-base font-extrabold text-slate-900">
                          {event.title}
                        </h3>
                        <Pill tone={cfg.tone}>
                          <IconComp className="h-3 w-3" />
                          {cfg.label}
                        </Pill>
                        {event.readonly && (
                          <span className="text-slate-400" title="Lecture seule">
                            <Lock className="h-3.5 w-3.5" />
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                        {event.customer_name && (
                          <span className="inline-flex items-center gap-1">
                            <Users className="h-3.5 w-3.5" />
                            {event.customer_name}
                          </span>
                        )}
                        {event.project_title && (
                          <span className="rounded-full bg-slate-50 px-2 py-1 ring-1 ring-slate-200">
                            {event.project_title}
                          </span>
                        )}
                        {event.amount_due != null && (
                          <span className="font-semibold text-violet-600">
                            {formatMoney(event.amount_due)}
                          </span>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => openEdit(event)}
                      className="rounded-2xl border border-slate-200 bg-white p-2 opacity-90 hover:bg-slate-50"
                      title={event.readonly ? "Voir" : "Modifier"}
                    >
                      {event.readonly ? (
                        <Eye className="h-4 w-4 text-slate-700" />
                      ) : (
                        <Pencil className="h-4 w-4 text-slate-700" />
                      )}
                    </button>
                  </div>

                  {event.description ? (
                    <p className="mt-3 line-clamp-2 text-sm text-slate-600">
                      {event.description}
                    </p>
                  ) : (
                    <p className="mt-3 text-sm text-slate-400 italic">
                      Aucune description.
                    </p>
                  )}

                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <div className="text-xs text-slate-500">Début</div>
                      <div className="mt-1 text-sm font-bold text-slate-900">
                        {formatDateTime(event.start_time)}
                      </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <div className="text-xs text-slate-500">Fin</div>
                      <div className="mt-1 text-sm font-bold text-slate-900">
                        {event.end_time ? formatDateTime(event.end_time) : "—"}
                      </div>
                    </div>
                  </div>

                  {event.location && (
                    <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
                      <MapPin className="h-3.5 w-3.5" />
                      {event.location}
                    </div>
                  )}

                  <button
                    onClick={() => openEdit(event)}
                    className="mt-5 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                  >
                    {event.readonly ? "Voir le détail" : "Ouvrir & modifier"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal Edition (événements manuels) */}
      {showModal && modalMode === "edit" && (
        <ModalShell
          title={editingEvent ? "Modifier l'événement" : "Nouvel événement"}
          subtitle="Planifie un rendez-vous ou une tâche"
          onClose={() => {
            setShowModal(false);
            setEditingEvent(null);
          }}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-slate-900">Titre *</label>
              <input
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                placeholder="Ex: Réunion client, Appel commercial..."
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold text-slate-900">Type</label>
                <select
                  value={formData.event_type}
                  onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                  disabled={isCampaignEvent}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                >
                  {editableTypes.map((k) => (
                    <option key={k} value={k}>
                      {eventTypeConfig[k]?.label || k}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-900">Rappel</label>
                <select
                  value={formData.reminder_minutes}
                  onChange={(e) =>
                    setFormData({ ...formData, reminder_minutes: parseInt(e.target.value) })
                  }
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                >
                  <option value={0}>Aucun rappel</option>
                  <option value={15}>15 min avant</option>
                  <option value={30}>30 min avant</option>
                  <option value={60}>1h avant</option>
                  <option value={1440}>1 jour avant</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold text-slate-900">Début *</label>
                <input
                  required
                  type="datetime-local"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-900">Fin</label>
                <input
                  type="datetime-local"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-900">Lieu</label>
              <input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                placeholder="Adresse ou lien visio..."
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold text-slate-900">Client (optionnel)</label>
                <select
                  value={formData.customer_id}
                  onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                  disabled={isCampaignEvent}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                >
                  <option value="">Aucun client</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} {c.phone ? `- ${c.phone}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-900">Projet (optionnel)</label>
                <select
                  value={formData.project_id}
                  onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                  disabled={isCampaignEvent}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                >
                  <option value="">Aucun projet</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-900">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                rows={3}
                placeholder="Notes, ordre du jour..."
              />
            </div>

            <div className="flex gap-3 pt-2">
              {editingEvent && !editingEvent.readonly && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting || saving}
                  className="rounded-2xl border border-rose-200 bg-white px-4 py-2.5 text-sm font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-70"
                >
                  {deleting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditingEvent(null);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50"
              >
                Annuler
              </button>

              {editingEvent && !editingEvent.readonly && editingEvent.status !== 'COMPLETED' && (
                <button
                  type="button"
                  onClick={handleMarkDone}
                  disabled={saving || deleting}
                  className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-70"
                >
                  ✓ Terminé
                </button>
              )}

              <button
                type="submit"
                disabled={saving || deleting}
                className="flex-1 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-70"
              >
                {saving ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Enregistrement...
                  </span>
                ) : editingEvent ? (
                  "Mettre à jour"
                ) : (
                  "Créer"
                )}
              </button>
            </div>
          </form>
        </ModalShell>
      )}

      {/* Modal Lecture seule (événements auto-générés) */}
      {showModal && modalMode === "view" && editingEvent && (
        <ModalShell
          title={editingEvent.title}
          subtitle={eventTypeConfig[editingEvent.event_type]?.label || "Événement"}
          onClose={() => {
            setShowModal(false);
            setEditingEvent(null);
          }}
        >
          <div className="space-y-4">
            {/* Badge type */}
            <div className="flex items-center gap-2">
              <Pill tone={eventTypeConfig[editingEvent.event_type]?.tone || "neutral"}>
                <Lock className="h-3 w-3" />
                {eventTypeConfig[editingEvent.event_type]?.label || editingEvent.event_type}
              </Pill>
              <span className="text-xs text-slate-500">Généré automatiquement</span>
            </div>

            {/* Description */}
            {editingEvent.description && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-semibold text-slate-500 mb-1">Description</div>
                <p className="text-sm text-slate-900">{editingEvent.description}</p>
              </div>
            )}

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-semibold text-slate-500 mb-1">Date / Heure</div>
                <p className="text-sm font-bold text-slate-900">{formatDateTime(editingEvent.start_time)}</p>
              </div>
              {editingEvent.amount_due != null && (
                <div className="rounded-2xl border border-violet-200 bg-violet-50 p-4">
                  <div className="text-xs font-semibold text-violet-600 mb-1">Montant dû</div>
                  <p className="text-sm font-bold text-violet-900">{formatMoney(editingEvent.amount_due)}</p>
                </div>
              )}
            </div>

            {/* Client */}
            {editingEvent.customer_name && (
              <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <Users className="h-5 w-5 text-slate-500" />
                <div>
                  <div className="text-xs font-semibold text-slate-500">Client</div>
                  <p className="text-sm font-bold text-slate-900">{editingEvent.customer_name}</p>
                </div>
              </div>
            )}

            {/* Projet */}
            {editingEvent.project_title && (
              <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <Briefcase className="h-5 w-5 text-slate-500" />
                <div>
                  <div className="text-xs font-semibold text-slate-500">Projet</div>
                  <p className="text-sm font-bold text-slate-900">{editingEvent.project_title}</p>
                </div>
              </div>
            )}

            {/* Statut */}
            {editingEvent.status && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-500">Statut:</span>
                <Pill tone={editingEvent.status === "paid" || editingEvent.status === "PAID" ? "green" : "yellow"}>
                  {editingEvent.status}
                </Pill>
              </div>
            )}

            {/* Boutons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditingEvent(null);
                }}
                className="flex-1 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50"
              >
                Fermer
              </button>

              {editingEvent.ref_type && editingEvent.ref_id && (
                <button
                  type="button"
                  onClick={() => navigateToRef(editingEvent)}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  <ExternalLink className="h-4 w-4" />
                  {editingEvent.ref_type === "INSTALLMENT_PLAN"
                    ? "Ouvrir l'échéancier"
                    : editingEvent.ref_type === "ORDER"
                    ? "Ouvrir la commande"
                    : "Ouvrir la prestation"}
                </button>
              )}
            </div>

            {editingEvent.event_type === "PAYMENT_DUE" && editingEvent.ref_type === "INSTALLMENT_PLAN" && (
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => navigate(`/installments?plan_id=${editingEvent.ref_id}${editingEvent.customer_uid ? `&customer_uid=${editingEvent.customer_uid}` : ''}`)}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-950"
                >
                  <ExternalLink className="h-4 w-4" />
                  Voir l'échéancier
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/installments?plan_id=${editingEvent.ref_id}${editingEvent.customer_uid ? `&customer_uid=${editingEvent.customer_uid}` : ''}`)}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                >
                  Encaisser
                </button>
              </div>
            )}
          </div>
        </ModalShell>
      )}
    </div>
  );
}
